'use client';

import Image from '@/components/Image';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useTranslations } from '@/i18n/use-translations';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useFrenchSummerTracking } from '../hooks/useFrenchSummerTracking';
import GoodItemPc from './GoodItemPc';
import GoodItemWap from './GoodItemWap';

// 商品数据类型定义
export interface GoodListProduct {
  id?: number | string;
  name?: string;
  short_name?: string;
  shop_sku?: string;
  business_sku?: string;
  image?: {
    product?: string[];
  };
  price_info?: Array<{
    currency?: string;
    price?: string;
    sell_price?: string;
  }>;
  is_sold_out?: boolean; // 是否售罄
  [key: string]: any;
}

// 促销图片数据类型
export interface PromoImageData {
  image?: string;
  wap_image?: any; // 兼容旧字符串或新对象
  pc_image?: {
    url: string;
    aspectRatio?: string;
  };
  title?: any; // 兼容旧字符串或新对象
  subtitle?: any; // 兼容旧字符串或新对象
  word?: string; // 移动端使用
  description?: string; // PC端使用
  aspectRatio?: string; // 宽高比
  jump_type?: number; // 跳转类型：1-商品列表，2-商品详情
  jump_value?: string; // 跳转值
  image_alt?: string; // 图片alt
  image_title?: string; // 图片标题
}

export interface GoodListProps {
  // 基本配置
  id?: string;
  className?: string;
  index?: number; // 商品列表索引，用于控制布局

  // 促销图片配置
  promoImage: PromoImageData;

  // 商品列表配置
  products: GoodListProduct[];

  // 设备类型
  deviceType: EPlatform;

  // 埋点相关
  tabName?: string; // 当前tab名称，用于埋点

  // 布局配置
  // 间距配置
  padding?: {
    wap?: string; // 移动端左右间距
    pc?: string; // PC端左右间距
  };

  // 事件回调
  onProductClick?: (product: GoodListProduct, index: number) => void;
  onProductExp?: (product: GoodListProduct, index: number) => void;
  onViewMoreClick?: () => void;

  // VIEW MORE按钮配置
  viewMoreConfig?: {
    groupId?: string;
    text?: string;
    showButton?: boolean;
  };
}

export default function GoodList({
  id,
  className = '',
  index = 0,
  promoImage,
  products = [],
  deviceType,
  tabName = 'unknown',
  padding = {
    wap: 'px-0',
    pc: 'px-[20px]',
  },
  onProductClick,
  onProductExp,
  onViewMoreClick,
  viewMoreConfig = { showButton: true, text: 'viewMore' },
}: GoodListProps) {
  // 判断设备类型
  const isWeb = deviceType === EPlatform.WEB;
  const t = useTranslations('activity.summerEdit') as any;

  // 获取埋点方法
  const { trackAdExp, trackViewMoreClick, trackAdClick } = useFrenchSummerTracking();

  // 促销图片曝光状态
  const [promoImageExposed, setPromoImageExposed] = useState(false);
  const promoImageRef = useRef<HTMLDivElement>(null);

  // 是否显示全部商品（已弃用，改为控制当前展示数量）
  const [displayCount, setDisplayCount] = useState<number>(isWeb ? 9 : 4);
  const INCREMENT_COUNT = isWeb ? 9 : 4;

  // 默认最多展示商品数量
  const MAX_INITIAL_PRODUCTS = isWeb ? 9 : 4;

  // 确保初始渲染时，如果设备类型变化导致数量不对，进行重置（防hydration不匹配也可以加useEffect）
  useEffect(() => {
    setDisplayCount(isWeb ? 9 : 4);
  }, [isWeb]);

  // 计算要显示的商品列表
  const displayedProducts = products.slice(0, displayCount);

  // 判断是否显示 VIEW MORE 按钮
  const shouldShowViewMoreButton = viewMoreConfig.showButton && products.length > displayCount;
  const promoJumpType = promoImage.jump_type ? promoImage.jump_type.toString() : '';
  const promoJumpContent = promoImage.jump_value || '';
  const hasPromoJump = Boolean(promoJumpType && promoJumpContent);

  const { ref } = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 促销图片曝光检测
  useEffect(() => {
    if (!promoImageRef.current || promoImageExposed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !promoImageExposed) {
            setPromoImageExposed(true);
            // 触发促销图片曝光埋点
            trackAdExp('商品版位', tabName, index, promoJumpType, promoJumpContent);
          }
        });
      },
      { threshold: 0.5 }, // 当促销图片50%可见时触发
    );

    observer.observe(promoImageRef.current);

    return () => {
      observer.disconnect();
    };
  }, [promoImageExposed, trackAdExp, index, tabName, promoJumpType, promoJumpContent]);

  // 判断是否为奇数索引（促销图片在右侧）
  const isOddIndex = index % 2 === 1;

  // 移除了复杂的吸顶逻辑，改用简单的sticky定位

  // 获取间距类名
  const getPaddingClassName = () => {
    const wapPadding = padding.wap || 'px-[14px]';
    const pcPadding =
      padding.pc || 'md:px-[48px] lg:px-[48px] xl:px-[48px] 2xl:px-[48px] 3xl:px-[48px]';

    // 根据设备类型返回相应的类名
    if (isWeb) {
      return `${pcPadding} mx-auto`;
    } else {
      return `${wapPadding} mx-auto`;
    }
  };

  // 处理 VIEW MORE 按钮点击
  const handleViewMoreClick = (e: React.MouseEvent) => {
    e.preventDefault();

    // 触发View More点击埋点
    trackViewMoreClick('商品版位', tabName, index);

    // 增加展示数量
    setDisplayCount((prevCount) => prevCount + INCREMENT_COUNT);
    onViewMoreClick?.();
  };

  // 处理商品点击
  const handleProductClick = (product: GoodListProduct, productIndex: number) => {
    onProductClick?.(product, productIndex);
  };

  // 处理商品曝光
  const handleProductInView = (product: GoodListProduct, productIndex: number) => {
    onProductExp?.(product, productIndex);
  };

  // 生成网格类名
  const getGridClassName = () => {
    // 移动端：使用 gap-x-[4px] 确保总间距为4px，商品各占50%宽度
    const wapGrid = `grid grid-cols-2 gap-x-[4px] gap-y-[4px]`;
    const pcGrid = `lg:grid lg:grid-cols-3 lg:gap-x-[12px] lg:gap-y-[12px]`;

    return `${wapGrid} ${pcGrid}`.trim();
  };

  const pcImageUrl = promoImage.pc_image?.url || promoImage.image;
  const wapImageUrl =
    typeof promoImage.wap_image === 'string'
      ? promoImage.wap_image
      : promoImage.wap_image?.url || promoImage.image;

  // 如果没有促销图片，显示占位区域
  if (!pcImageUrl && !wapImageUrl) {
    return (
      <div className="good-list w-full">
        <div className={`${getPaddingClassName()} py-8`}>
          <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center">
            <p className="text-gray-500">no data</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id={id} className={`good-list w-full ${className} mb-[30px] md:mb-[24px] relative`}>
      <div
        className={`${getPaddingClassName()}
        flex flex-col gap-0
        lg:flex-row lg:gap-[12px] ${isOddIndex ? 'lg:flex-row-reverse' : ''}
      `}
      >
        {/* 促销图片区域 - 使用简单的sticky定位 */}
        <div className="w-full lg:w-[32.1875vw] flex-shrink-0 relative mb-[4px] lg:mb-0">
          <div
            ref={promoImageRef}
            className={`promo-image-wrap overflow-hidden ${
              isWeb ? 'rounded-[12px]' : ''
            } w-full ${isWeb ? 'lg:sticky' : 'relative'} ${hasPromoJump ? 'cursor-pointer' : ''}`}
            onClick={() => {
              if (!hasPromoJump) return;

              // 触发促销图点击埋点
              trackAdClick('商品版位', tabName, index, promoJumpType, promoJumpContent);

              // 执行跳转
              if (promoImage.jump_type === 1) {
                // 跳转商品列表
                window.location.href = promoJumpContent;
              } else if (promoImage.jump_type === 2) {
                // 跳转商品详情
                window.location.href = `/goods-detail/${promoJumpContent}`;
              } else {
                window.location.href = promoJumpContent;
              }
            }}
            style={{
              height: 'auto',
              aspectRatio: isWeb
                ? promoImage.pc_image?.aspectRatio?.replace('/', ' / ') || '618 / 660'
                : promoImage.wap_image?.aspectRatio?.replace('/', ' / ') || '750 / 562',
              ...(isWeb && {
                top: 'calc(100px + 1vw)',
              }),
            }}
          >
            <div className="w-full h-full flex items-center justify-center absolute">
              <Image
                src={isWeb ? pcImageUrl : wapImageUrl}
                alt={promoImage.image_alt || ''}
                fill
                className="object-cover"
                priority
                sizes={isWeb ? '420px' : '100vw'}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[rgba(0,0,0,0.5)] via-transparent to-transparent pointer-events-none" />

              <div
                className={`absolute bottom-0 left-0 w-full flex flex-col justify-end pointer-events-none ${
                  isWeb ? 'px-[24px] pb-[20px]' : 'px-[22px] pb-[12px]'
                }`}
              >
                {(promoImage.image_title ||
                  promoImage.title?.text ||
                  (typeof promoImage.title === 'string' ? promoImage.title : null)) && (
                  <h3
                    className={`font-bb whitespace-pre-wrap ${
                      isWeb
                        ? 'text-[24px] leading-[36px] mb-[12px] text-left'
                        : 'text-[16px] leading-[19px] mb-[12px] text-center'
                    }`}
                    style={{ color: promoImage.title?.color || '#FFFFFF' }}
                  >
                    {promoImage.image_title ||
                      promoImage.title?.text ||
                      (typeof promoImage.title === 'string' ? promoImage.title : null)}
                  </h3>
                )}
                {(promoImage.word ||
                  promoImage.subtitle?.text ||
                  (typeof promoImage.subtitle === 'string' ? promoImage.subtitle : null)) && (
                  <p
                    className={`text-center whitespace-pre-wrap ${
                      isWeb
                        ? 'text-[16px] leading-[24px] text-left'
                        : 'text-[11px] leading-[13px] text-center'
                    }`}
                    style={{ color: promoImage.subtitle?.color || '#FFFFFF' }}
                  >
                    {promoImage.word ||
                      promoImage.subtitle?.text ||
                      (typeof promoImage.subtitle === 'string' ? promoImage.subtitle : null)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 商品列表区域 */}
        <div className="w-full flex-1 relative px-[4px] md:px-0">
          {products.length > 0 ? (
            <div>
              {/* 商品网格 */}
              <div className={getGridClassName()}>
                {displayedProducts.map((product, productIndex) => {
                  return (
                    <div
                      key={`${product.shop_sku || product.id}-${productIndex}`}
                      className="w-full"
                    >
                      {isWeb ? (
                        <GoodItemPc
                          product={product}
                          index={productIndex}
                          tabName={tabName}
                          deviceType={deviceType}
                          onProductClick={handleProductClick}
                          onProductExp={handleProductInView}
                        />
                      ) : (
                        <GoodItemWap
                          product={product}
                          index={productIndex}
                          tabName={tabName}
                          deviceType={deviceType}
                          onProductClick={handleProductClick}
                          onProductExp={handleProductInView}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* VIEW MORE按钮 */}
              {shouldShowViewMoreButton && (
                <div className="flex justify-center mt-[16px] lg:mt-[32px]">
                  <div
                    ref={ref}
                    className={`view-more flex items-center justify-center font-bb text-[#282828] cursor-pointer transition-all duration-200 hover:opacity-90 no-underline bg-transparent ${
                      isWeb
                        ? 'w-[338px] h-[48px] rounded-[1.81vw] border border-[#808080] text-[16px]'
                        : 'w-[282px] h-[43px] rounded-[50px] border border-[#808080] text-[14px]'
                    }`}
                    style={{
                      lineHeight: isWeb ? '1.32vw' : '17px',
                    }}
                    aria-label={viewMoreConfig.text}
                    onClick={handleViewMoreClick}
                  >
                    {t('viewMore')}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <p className="text-gray-500">{t('noData')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
